// The City College of New York: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CCNY's Common Data Set 2025-2026 (Institutional Research, file
//    20260716_CDS_2025-2026_UPDATED.pdf; ccny.cuny.edu refuses plain
//    downloads, so it was fetched in a browser): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table (no Unknown counts),
//    C8 (SAT or ACT considered if submitted for Fall 2027, scores by
//    February 1), C21/C22 (no early decision or early action), G1/G5
//    2026-2027 costs. Quirk: section G checks the box saying final
//    2026-2027 costs were not yet available (expected 6/1) yet lists
//    figures; they are shown as published and the note says so. The
//    Bursar's Tuition Listing for Fall 2026-Spring 2027 agrees on tuition
//    ($3,465 a semester for New York State residents, $620 an hour for
//    others). G5 gives no transportation figure.
//  - ccny.cuny.edu First-Year (Freshman) Students page (the review and the
//    selective programs) and Grove School of Engineering admissions;
//    ssa.ccny.cuny.edu Undergraduate Application Instructions (B Arch
//    Creative Challenge).
//  - cuny.edu How to Apply (fall priority deadline, decision timing and the
//    deadlines for Macaulay, Spitzer, Grove and the BS/MD program) and the
//    Standardized Testing Policy. CUNY lists these dates for fall admission
//    in general rather than naming fall 2027.
//  - College Scorecard API, school id 190567 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'City College Common Data Set 2025-2026',
        url: 'https://www.ccny.cuny.edu/sites/default/files/2026-07/20260716_CDS_2025-2026_UPDATED.pdf',
        read: '2026-10-01',
    },
    freshman: {
        label: 'City College Admissions, Freshman Students',
        url: 'https://www.ccny.cuny.edu/admissions/freshman-students',
        read: '2026-10-01',
    },
    cunyApply: {
        label: 'CUNY Admissions, How to Apply (Deadlines and Notification Dates)',
        url: 'https://www.cuny.edu/admissions/undergraduate/apply/',
        read: '2026-10-01',
    },
    testPolicy: {
        label: 'CUNY Admissions, Standardized Testing Policy',
        url: 'https://www.cuny.edu/admissions/undergraduate/apply/covid-19-standardized-tests/',
        read: '2026-10-01',
    },
    engineering: {
        label: 'Grove School of Engineering, Admissions: Overview',
        url: 'https://www.ccny.cuny.edu/engineering/admissions',
        read: '2026-10-01',
    },
    architecture: {
        label: 'Spitzer School of Architecture, Undergraduate Application Instructions',
        url: 'https://ssa.ccny.cuny.edu/admissions/undergraduate/ug-b-arch-admissions/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190567-CUNY-City-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'city-college-of-new-york',
    kbId: 'city_college_of_new_york',
    name: 'The City College of New York',
    shortName: 'City College',
    location: 'New York, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CCNY logos and branding); decoration only.
    branding: {
        primary: '#775BA6',
        secondary: '#54585A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/city_college_of_new_york_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 34284,
        admitted: 19880,
        enrolled: 2700,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 31076, admitted: 18479 },
            { group: 'Out-of-state', applied: 2038, admitted: 1084 },
            { group: 'International', applied: 1170, admitted: 317 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'City College\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'City College has no early decision or early action plan; the Common Data Set answers no to both.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'City College reviews your overall high school average, your grades in core subjects such as math, English and science, and SAT or ACT scores if you send them, with an eye to your intended major. Some programs ask for more:',
        rows: [
            { school: 'Grove School of Engineering', requirement: 'An overall high school average of 85 or better; four years of high school math, including pre-calculus, averaging 80 or better; a year of physics and a year of chemistry, each at 80 or better (a year-long college course can stand in for either); and two years of English.', source: 'engineering' },
            { school: 'Spitzer School of Architecture (B Arch)', requirement: 'The Creative Challenge, due February 1 along with the CUNY application, for fall entry only. The school looks for at least an 80 overall high school average, and decisions come in March. Challenges received after February 1 and through May 1 go straight to the waitlist.', source: 'architecture' },
            { school: 'BS/MD (Sophie Davis School of Biomedical Education)', requirement: 'A December 1 deadline and a supplemental application on the college\'s website.', source: 'cunyApply' },
        ],
        note: 'Advertising and Public Relations, Electronic Design and Multimedia, Music, Premedical Studies and Sonic Arts also list additional materials or steps on City College\'s freshman page.',
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 1' },
        rows: [
            { plan: 'First-year (U.S. educated)', deadline: 'February 1', decision: 'Rolling, January to July', binding: false },
            { plan: 'Macaulay Honors College', deadline: 'November 16', decision: 'March 15', binding: false },
            { plan: 'BS/MD program', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Spitzer School of Architecture', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'You apply through the CUNY application, which takes up to six CUNY colleges; the freshman fee is $65. February 1 is the priority deadline for the application and supporting documents; after it, colleges that still accept applications decide as space allows. Applicants educated outside the U.S. apply by December 1, with documents by December 15. Macaulay applicants hear first about general admission, from mid-February, and about Macaulay on March 15. Macaulay also has an Early Decision deadline of October 26.',
        source: 'cunyApply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CUNY is test-optional: you choose whether to send SAT or ACT scores, and leaving them out cannot be used to deny you admission. Decisions rest mainly on your high school record: your average, your course grades and the rigor of your courses.',
        source: 'testPolicy',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6930, 18600] },
            { item: 'Required fees', amounts: [410, 410] },
            { item: 'Food and housing (campus housing)', amounts: [19552, 19552] },
            { item: 'Books and supplies', amounts: [1500, 1500] },
            { item: 'Other expenses', amounts: [6283, 6283] },
            { item: 'Transportation', amounts: [null, null] },
        ],
        totals: [34675, 46345],
        note: 'For a student in campus housing. The Common Data Set lists these 2026-2027 figures while marking its final 2026-2027 costs as not yet available, so they are shown as published. It gives no transportation figure, so the totals leave it out. Out-of-state tuition applies to students who are not New York State residents.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 1557,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: 654 },
                { income: '$48,001 to $75,000', amount: 3795 },
                { income: '$75,001 to $110,000', amount: 7139 },
                { income: 'Over $110,000', amount: 10618 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 band. Your own price comes from City College\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
